import isNil from 'lodash/isNil';

import type {
  QuerySentimentV3Query,
  SentimentV3RespData,
} from '@revelio/data-access';
import type { BarData } from '@revelio/replots';

import { topics } from '../../../../sentiment/sentimentV2/topics-subfilter/topics';
import type { SentimentChartType } from '../../PageConfigProvider/types/chart';
import type { SentimentData } from '../sentiment-v3-data-provider';
import { DataType, type SingleDataSet, type TimeseriesDataSet } from '../types';

type SentimentMetricKeys = keyof Omit<SentimentV3RespData, '__typename'>;
type SentimentMetrics = SentimentMetricKeys | 'sentiment_top_bottom';
type SentimentV3Entities = NonNullable<
  QuerySentimentV3Query['sentiment_v3']
>['entities'];
type SentimentV3Entity = SentimentV3Entities[number];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

type SentimentDriversData = Record<
  SentimentMetricKeys,
  BarData<{ count: number | null; date?: string; rawValue?: number | null }>[]
>;
const toBarChartData = (entity: SentimentV3Entity) => {
  const label = entity.short_name;
  const barChartData: Partial<SentimentDriversData> = {};

  topics.forEach((topic) => {
    const record = entity.metrics;
    const series = record[topic.value] ?? [];
    const last = series[series.length - 1];

    const weight = last?.weight ?? 0;
    const count = Math.max(1, Math.round(weight));

    barChartData[topic.value] = [
      {
        label,
        count,
        value: isFiniteNumber(last?.value) ? last.value / 100 : null,
      },
    ];
  });

  return barChartData;
};

const transformToSingleEntity = (data: Partial<SentimentDriversData>) => {
  const sortedData = Object.entries(data)
    .filter(
      ([key, value]) =>
        key !== 'overall' &&
        Array.isArray(value) &&
        value.length > 0 &&
        !isNil(value[0]?.count) &&
        value?.[0]?.count > 0
    )
    .flatMap(([key, values]) =>
      values.slice(0, 1).map((value) => ({
        date: value.date,
        label: topics.find((t) => t.value === key)?.label ?? key,
        rawValue: value.value,
        value: (value.value ?? 0.5) - 0.5,
        count: value.count,
      }))
    )
    .sort(
      (a, b) =>
        Math.abs(b.value) * Math.sqrt(b.count ?? 0) -
        Math.abs(a.value) * Math.sqrt(a.count ?? 0)
    )
    .slice(0, 20)
    .sort((a, b) => b.value - a.value);

  return sortedData;
};

export const convertSentimentV3Data = ({
  data,
  chartType,
  metric,
  monthRefs,
  selectedEntityId,
}: {
  data: SentimentV3Entities;
  chartType: SentimentChartType;
  metric: SentimentMetrics;
  monthRefs: string[];
  selectedEntityId?: string;
}): SentimentData => {
  if (metric === 'sentiment_top_bottom') {
    const selected = selectedEntityId
      ? data.find((entity) => String(entity.id) === selectedEntityId)
      : data[0];

    const singleEntityData = selected
      ? transformToSingleEntity(toBarChartData(selected))
      : [];

    const singleDataSet: SingleDataSet<{ count?: number }> = {
      type: DataType.SINGLE,
      data: singleEntityData.map((item) => ({
        dim: item.label,
        value: item.value,
        count: item.count ?? undefined,
      })),
    };

    return singleDataSet;
  }

  if (chartType === 'line') {
    const mapped: TimeseriesDataSet['data'] = data
      .map((entity) => {
        const dim = entity.short_name;
        const metricSeries = entity.metrics[metric];
        if (!dim) return null;

        const timeseries = metricSeries
          .map((point) => {
            const date = monthRefs[point.month_id];
            if (!date) return null;
            return { date, value: (point.value ?? 0) / 100 };
          })
          .filter((point) => point !== null);

        return { id: String(entity.id), dim, timeseries };
      })
      .filter((point) => point !== null);

    return { type: DataType.TIMESERIES, data: mapped };
  }

  const mapped: SingleDataSet<{ count?: number }>['data'] = data
    .map((entity) => {
      const dim = entity.short_name;
      const metricSeries = entity.metrics[metric];
      const last = metricSeries[metricSeries.length - 1];
      const value = (last?.value ?? 0) / 100;
      const weight = last?.weight ?? 0;
      const count = Math.max(1, Math.round(weight));
      if (!dim) return null;
      return { id: String(entity.id), dim, value, count };
    })
    .filter((point) => point !== null);

  return { type: DataType.SINGLE, data: mapped };
};
